import { Box, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import TextButton from "../../../../components/buttons/TextButton";
import CustomTooltip from "../../../../components/CustomTooltip";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { getPrettyDateTime } from "../../../../utils/timestamp";
import { inputIcons, logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import { MicrosoftADFiles } from "./constants";
import GroupPolicyExportStep from "./GroupPolicyExportStep";
import InventoryUploadStep from "./InventoryUploadStep";
import OnPremSetupStep from "./OnPremSetupStep";

const MicrosoftActiveDirectoryIntegration = () => {
  const {
    registerMicrosoftActiveDirectoryOfflineIntegration,
    registerMicrosoftActiveDirectoryOnPremIntegration,
    getMicrosoftActiveDirectoryStatus,
  } = useAPI();

  const [searchParams] = useSearchParams();
  const guideMode = searchParams.has("viewonly");
  const [currentStep, setCurrentStep] = useState(1);
  const [showSkipConfirmDialog, setShowSkipConfirmDialog] = useState(false);
  const [showConfig, setShowConfig] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isOnPremLoading, setIsOnPremLoading] = useState(false);
  const colors = useColors();
  const queryClient = useQueryClient();

  const totalSteps = 2;

  // Use useQuery to fetch Microsoft Active Directory status
  const {
    data: statusData,
    isLoading: isStatusLoading,
    isSuccess: isStatusSuccess,
  } = useQuery({
    queryKey: ["microsoft-active-directory-status"],
    queryFn: getMicrosoftActiveDirectoryStatus,
    select: (data) => data.data,
  });

  const { isIntegrated, isIntegratedLoading, isIntegratedSuccess } =
    useIsIntegrated("microsoft_active_directory");

  const registerOfflineMutation = useMutation({
    mutationFn: registerMicrosoftActiveDirectoryOfflineIntegration,
    onSuccess: () => {
      setShowConfig(false);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Microsoft Active Directory integration successful");
    },
    onError: () => {
      Toast.error({
        text: "Integration failed",
        comment: "Something went wrong. Please try again.",
      });
    },
  });

  const handleOnPremSubmit = async (values) => {
    setIsOnPremLoading(true);
    try {
      await registerMicrosoftActiveDirectoryOnPremIntegration(
        values.DomainControllerPrimaryIp,
        values.DomainControllerSecondaryIp,
        values.adDomainName,
        values.username,
        values.password
      );
      setShowConfig(false);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success(
        "Microsoft Active Directory on-prem configured successfully"
      );
    } catch (error) {
      Toast.error({
        text: "Integration failed",
        comment: "Something went wrong. Please try again.",
      });
    } finally {
      setIsOnPremLoading(false);
    }
  };

  const loading =
    isStatusLoading ||
    isIntegratedLoading ||
    registerOfflineMutation.isPending ||
    isOnPremLoading;

  const success = isStatusSuccess && isIntegratedSuccess;

  let lastUpdated = "";

  if (success) {
    lastUpdated = statusData?.last_updated;
  }

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canPartialSubmit = () => {
    // Skip is enabled only if the 3 HTML files (Domain Controller, Server, Endpoint) are uploaded
    return (
      statusData?.file_mapping?.[MicrosoftADFiles.DOMAIN_CONTROLLER.key]
        ?.uploaded &&
      statusData?.file_mapping?.[MicrosoftADFiles.SERVER.key]?.uploaded &&
      statusData?.file_mapping?.[MicrosoftADFiles.ENDPOINT.key]?.uploaded &&
      !canSubmit()
    );
  };

  const canSubmit = () => {
    // Submit is enabled only if all 5 files are uploaded
    return Object.values(statusData?.file_mapping).every(
      (file) => file.uploaded
    );
  };

  const handleSkipButtonClick = () => {
    setShowSkipConfirmDialog(true);
  };

  const handleSkipConfirm = () => {
    setShowSkipConfirmDialog(false);
    handleFinishImport();
  };

  const handleSkipCancel = () => {
    setShowSkipConfirmDialog(false);
  };

  const handleFinishImport = () => {
    if (!isIntegrated) {
      registerOfflineMutation.mutate();
    } else {
      // If already integrated, just close the config
      setShowConfig(false);
      Toast.success("Configuration updated successfully");
    }
  };

  const getTitle = () => {
    if (isIntegrated && !guideMode) {
      return "Microsoft Active Directory is integrated";
    }
    return "Import data from Microsoft Active Directory";
  };

  const getDescription = () => {
    if (isIntegrated && !guideMode) {
      return "Upload a new configuration file to update your Microsoft Active Directory integration. This will replace the current configuration with the new one.";
    }
    return "To help Breeze detect misconfigurations and security gaps, export specific data from your Microsoft Active Directory environment and import it in the guide below.";
  };

  const getButtonText = () => {
    if (isIntegrated) {
      return "Update Configuration";
    }
    return "Finish Import Setup";
  };

  return (
    <Box>
      <Guideline
        title={getTitle()}
        image={logos.MicrosoftActiveDirectoryLogo}
        name={"Microsoft Active Directory"}
        xs={12}
        content={
          loading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode && !showConfig ? null : (
            <Box display="flex" flexDirection="column" gap={2}>
              {guideMode && <IntegrationGuideAlert />}

              <Typography variant={"body1"} color={colors.text_secondary}>
                {getDescription()}
              </Typography>

              <Tabs
                value={activeTab}
                onChange={(_event, newValue) => {
                  setActiveTab(newValue);
                }}
                sx={{
                  textTransform: "none",
                  "& .MuiTabs-indicator": {
                    backgroundColor: colors.primary_main,
                  },
                }}
              >
                <Tab label="File Upload" sx={{ textTransform: "none" }} />
                <Tab label="On Premise Setup" sx={{ textTransform: "none" }} />
              </Tabs>

              <Divider />

              {activeTab === 0 && (
                <>
                  {/* STEP INDICATOR */}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant={"body2"} color={colors.text_secondary}>
                      Step {currentStep} of {totalSteps}
                    </Typography>
                    {lastUpdated && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <SvgToIcon
                          IconLogo={inputIcons.PrimaryInfoIcon}
                          width={16}
                        />
                        <Typography variant="caption">
                          Last updated:{" "}
                          {getPrettyDateTime(lastUpdated, true)
                            ? getPrettyDateTime(lastUpdated, true)
                            : "N/A"}
                          .
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* STEP CONTENT */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    overflow={currentStep === 1 ? "visible" : "hidden"}
                    height={currentStep === 1 ? "auto" : "0px"}
                  >
                    <GroupPolicyExportStep statusData={statusData} />
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    overflow={currentStep === 2 ? "visible" : "hidden"}
                    height={currentStep === 2 ? "auto" : "0px"}
                  >
                    <InventoryUploadStep statusData={statusData} />
                  </Box>

                  <Divider />

                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                    marginBottom={2}
                  >
                    <SvgToIcon IconLogo={inputIcons.infoIcon} width={16} />
                    <Typography
                      variant={"caption"}
                      color={colors.text_secondary}
                    >
                      Breeze saves your progress — you can return anytime.
                    </Typography>
                  </Box>

                  {/* NAVIGATION BUTTONS */}
                  <Box display="flex" flexDirection="row" gap={2} marginTop={3}>
                    <Box>
                      {currentStep > 1 && (
                        <TextButton onClick={handlePreviousStep} size="medium">
                          Back
                        </TextButton>
                      )}
                    </Box>
                    <Box display="flex" gap={2} marginLeft="auto">
                      {currentStep < totalSteps && (
                        <ContainedButton
                          onClick={handleNextStep}
                          color="primary"
                          size="medium"
                        >
                          Continue to Inventory Upload
                        </ContainedButton>
                      )}
                      {currentStep === totalSteps && !isIntegrated && (
                        <Box display="flex" flexDirection="row" gap={2}>
                          <TextButton
                            color="primary"
                            onClick={handleSkipButtonClick}
                            size="medium"
                            disabled={!canPartialSubmit() || loading}
                          >
                            Skip Inventory Collection & Finish
                          </TextButton>
                          {!canSubmit() ? (
                            <CustomTooltip
                              title="All required files must be uploaded before you can finish setup."
                              placement="top"
                            >
                              <Box>
                                <ContainedButton
                                  onClick={handleFinishImport}
                                  color="primary"
                                  size="medium"
                                  disabled={!canSubmit() || loading}
                                >
                                  {loading ? (
                                    <LoadingIcon size={20} />
                                  ) : (
                                    getButtonText()
                                  )}
                                </ContainedButton>
                              </Box>
                            </CustomTooltip>
                          ) : (
                            <ContainedButton
                              onClick={handleFinishImport}
                              color="primary"
                              size="medium"
                              disabled={!canSubmit() || loading}
                            >
                              {loading ? (
                                <LoadingIcon size={20} />
                              ) : (
                                getButtonText()
                              )}
                            </ContainedButton>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {activeTab === 1 && (
                <OnPremSetupStep
                  onSubmit={handleOnPremSubmit}
                  guideMode={guideMode}
                  isLoading={isOnPremLoading}
                />
              )}
            </Box>
          )
        }
      />

      {/* Skip Confirmation Dialog */}
      <ConfirmationDialog
        openDialog={showSkipConfirmDialog}
        handleDialogClose={handleSkipCancel}
        handleDialogCancel={handleSkipCancel}
        handleConfirm={handleSkipConfirm}
        dialogTitle="Skip Inventory Collection?"
        dialogContentFirstLine="Skipping inventory collection limits Attack Surface Analysis and reduces visibility."
        dialogContentSecondLine="We recommend completing this step to ensure full analysis."
        dialogContentThirdLine="You can upload the inventory files later from the integration configuration."
        confirmationButtonText="Skip & Finish"
        cancelButtonText="Cancel"
        confirmationButtonEnabled={true}
        isLoading={loading}
        isLoadingText="Submitting..."
        destructive={true}
      />
    </Box>
  );
};

export default MicrosoftActiveDirectoryIntegration;
